import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { EmptyState, ErrorState, RowsSkeleton } from "@/components/States";
import ProspectForm from "@/components/ProspectForm";
import ProspectDrawer from "@/components/ProspectDrawer";
import { statusBadge, CHANNEL_META, STATUS_META, STATUS_OPTIONS, CRM_STAGES, stageOf, relativeTime, LEAD_LABEL } from "@/lib/format";

const LeadMark = ({ p }) => (LEAD_LABEL[p.lead_label]
  ? <span title={`${LEAD_LABEL[p.lead_label].label}${p.label_reason ? ` · ${p.label_reason}` : ""}`} aria-label={LEAD_LABEL[p.lead_label].label} data-testid={`crm-lead-label-${p.id}`} className="mr-1">{LEAD_LABEL[p.lead_label].emoji}</span>
  : null);
import { Search, Plus, Users, List, Columns3, Trash2, X } from "lucide-react";

const selectCls = "ca-select max-w-[190px]";

export default function Crm() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("recent");
  const [label, setLabel] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const debounce = useRef(null);
  const [view, setView] = useState(() => { try { return localStorage.getItem("ca_crm_view") || "lista"; } catch { return "lista"; } });
  const [picked, setPicked] = useState(() => new Set());
  const [dragOver, setDragOver] = useState("");
  const changeView = (v) => { setView(v); setPicked(new Set()); try { localStorage.setItem("ca_crm_view", v); } catch { /* opcional */ } };

  const load = useCallback(() => {
    setError(false);
    api
      .get("/prospects", { params: { search, channel, status, sort, lead_label: label } })
      .then((r) => setItems(r.data))
      .catch(() => setError(true));
  }, [search, channel, status, sort, label]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(debounce.current);
  }, [load, search]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("ca:data-changed", handler);
    return () => window.removeEventListener("ca:data-changed", handler);
  }, [load]);

  const openRow = (p) => { setSelected(p); setDrawerOpen(true); };

  const onCreated = (p) => setItems((cur) => [p, ...(cur || [])]);

  const onUpdated = (p) => {
    setItems((cur) => (cur || []).map((x) => (x.id === p.id ? p : x)));
    setSelected(p);
  };

  const onDeleted = (p) => {
    setItems((cur) => (cur || []).filter((x) => x.id !== p.id));
    let undone = false;
    const timer = setTimeout(() => {
      if (!undone) api.delete(`/prospects/${p.id}`).catch((err) => toast.error(apiError(err)));
    }, 5000);
    toast(`${p.name} eliminado`, {
      duration: 5000,
      action: {
        label: "Deshacer",
        onClick: () => { undone = true; clearTimeout(timer); setItems((cur) => [p, ...(cur || [])]); toast.success("Restaurado."); },
      },
    });
  };

  // Mover a una etapa: si ya está en ella no cambia; si no, pasa al primer estado de la etapa.
  const moveTo = async (p, stageKey) => {
    const st = CRM_STAGES.find((x) => x.key === stageKey);
    if (!st || st.statuses.includes(p.status)) return true;
    const to = st.statuses[0];
    setItems((cur) => (cur || []).map((x) => (x.id === p.id ? { ...x, status: to } : x)));
    try {
      const r = await api.put(`/prospects/${p.id}`, { status: to });
      setItems((cur) => (cur || []).map((x) => (x.id === p.id ? r.data : x)));
      return true;
    } catch (err) {
      setItems((cur) => (cur || []).map((x) => (x.id === p.id ? p : x)));
      toast.error(apiError(err));
      return false;
    }
  };

  const togglePick = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pickedItems = (items || []).filter((p) => picked.has(p.id));
  const allPicked = items && items.length > 0 && picked.size === items.length;

  const bulkMove = async (stageKey) => {
    if (!stageKey || !pickedItems.length) return;
    const res = await Promise.all(pickedItems.map((p) => moveTo(p, stageKey)));
    const ok = res.filter(Boolean).length;
    toast.success(`${ok} ${ok === 1 ? "contacto movido" : "contactos movidos"} a «${CRM_STAGES.find((x) => x.key === stageKey)?.label}».`);
    setPicked(new Set());
  };

  const bulkDelete = () => {
    const list = pickedItems;
    if (!list.length) return;
    const ids = new Set(list.map((p) => p.id));
    setItems((cur) => (cur || []).filter((x) => !ids.has(x.id)));
    setPicked(new Set());
    let undone = false;
    const timer = setTimeout(() => {
      if (!undone) list.forEach((p) => api.delete(`/prospects/${p.id}`).catch((err) => toast.error(apiError(err))));
    }, 5000);
    toast(`${list.length} ${list.length === 1 ? "contacto eliminado" : "contactos eliminados"}`, {
      duration: 5000,
      action: {
        label: "Deshacer",
        onClick: () => { undone = true; clearTimeout(timer); setItems((cur) => [...list, ...(cur || [])]); toast.success("Restaurados."); },
      },
    });
  };

  const onDrop = (e, stageKey) => {
    e.preventDefault();
    setDragOver("");
    const id = e.dataTransfer.getData("text/plain");
    const p = (items || []).find((x) => x.id === id);
    if (p) moveTo(p, stageKey);
  };

  const hasFilters = search || channel || status || label;

  return (
    <div className="space-y-8">
      <PageHeader
        title="CRM y contactos"
        subtitle="Todas tus relaciones, su estado y el siguiente paso. Pulsa una fila para ver la ficha."
        testId="crm-header"
        action={
          <button onClick={() => setFormOpen(true)} data-testid="crm-new-button" className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110">
            <Plus size={16} /> Nuevo contacto
          </button>
        }
      />

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="ca-seg" role="tablist" aria-label="Vista del CRM" data-testid="crm-view-toggle">
          <button type="button" role="tab" aria-selected={view === "lista"} className={view === "lista" ? "is-on" : ""} onClick={() => changeView("lista")} data-testid="crm-view-list"><List size={14} className="mr-1 inline" />Lista</button>
          <button type="button" role="tab" aria-selected={view === "etapas"} className={view === "etapas" ? "is-on" : ""} onClick={() => changeView("etapas")} data-testid="crm-view-stages"><Columns3 size={14} className="mr-1 inline" />Etapas</button>
        </div>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ca-subtle)]" />
          <input
            data-testid="crm-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o empresa…"
            className="ca-input pl-9"
          />
        </div>
        <select data-testid="crm-channel-filter" className={selectCls} value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">Todas las redes</option>
          {Object.entries(CHANNEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="crm-status-filter" className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <select data-testid="crm-label-filter" className={selectCls} value={label} onChange={(e) => setLabel(e.target.value)}>
          <option value="">Todo el interés</option>
          {Object.entries(LEAD_LABEL).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
        </select>
        <select data-testid="crm-sort" className={selectCls} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="recent">Más recientes</option>
          <option value="oldest">Más antiguos</option>
          <option value="score">Mayor encaje</option>
          <option value="interest">Más interesados primero</option>
          <option value="name">Por nombre</option>
        </select>
      </div>

      {items === null && !error && <RowsSkeleton rows={6} />}
      {error && <ErrorState description="No hemos podido cargar tus contactos." onRetry={load} testId="crm-error" />}

      {items && items.length === 0 && !error && (
        <EmptyState
          icon={Users}
          title={hasFilters ? "Ningún contacto con esos filtros" : "Aún no tienes contactos"}
          description={hasFilters ? "Prueba a quitar algún filtro o a buscar otra cosa." : "Busca oportunidades y guarda las que te interesen, o añade un contacto a mano con «Nuevo contacto»."}
          action={
            hasFilters ? (
              <button onClick={() => { setSearch(""); setChannel(""); setStatus(""); setLabel(""); }} className="rounded-md border border-brand-line bg-brand-panel px-4 py-2 text-sm font-semibold text-brand-ink transition-all duration-150 hover:border-brand-accent hover:text-brand-accent">
                Quitar filtros
              </button>
            ) : (
              <button onClick={() => setFormOpen(true)} className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-4 py-2 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110">
                <Plus size={15} /> Nuevo contacto
              </button>
            )
          }
          testId="crm-empty"
        />
      )}

      {items && items.length > 0 && view === "etapas" && (
        <div className="-mx-1 overflow-x-auto pb-2" data-testid="crm-kanban">
          <div className="grid min-w-[1080px] grid-cols-6 gap-3 px-1">
            {CRM_STAGES.map((st) => {
              const col = items.filter((p) => stageOf(p.status).key === st.key);
              return (
                <section key={st.key} data-testid={`crm-col-${st.key}`}
                  onDragOver={(e) => { e.preventDefault(); if (dragOver !== st.key) setDragOver(st.key); }}
                  onDragLeave={() => setDragOver((d) => (d === st.key ? "" : d))}
                  onDrop={(e) => onDrop(e, st.key)}
                  className="flex min-h-[220px] flex-col rounded-xl border p-2.5 transition-colors"
                  style={{ borderColor: dragOver === st.key ? "rgba(224,185,112,.55)" : "var(--ca-line)", background: dragOver === st.key ? "rgba(224,185,112,.06)" : "var(--ca-surface)" }}>
                  <header className="mb-2 flex items-center justify-between px-1.5 pt-1">
                    <b className="text-[13px] font-semibold text-[var(--ca-text)]">{st.label}</b>
                    <span className="ca-pill tnum" style={{ padding: "2px 8px" }}>{col.length}</span>
                  </header>
                  <div className="flex flex-1 flex-col gap-2">
                    {col.map((p) => {
                      const sb = statusBadge(p.status);
                      return (
                        <article key={p.id} draggable data-testid={`crm-kcard-${p.id}`}
                          onDragStart={(e) => { e.dataTransfer.setData("text/plain", p.id); e.dataTransfer.effectAllowed = "move"; }}
                          onClick={() => openRow(p)}
                          className="cursor-grab rounded-lg border border-brand-line bg-brand-panel p-3 transition-colors hover:border-brand-accent/40 active:cursor-grabbing">
                          <div className="flex items-center gap-2">
                            <InitialsAvatar name={p.name} size={28} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[13px] font-semibold text-brand-ink"><LeadMark p={p} />{p.name}</p>
                              <p className="truncate text-[12px] text-brand-muted">{CHANNEL_META[p.channel]?.label}{p.company ? ` · ${p.company}` : ""}</p>
                            </div>
                          </div>
                          <div className="mt-2"><span className={sb.cls}>{sb.label}</span></div>
                          {p.next_step && <p className="mt-2 line-clamp-2 text-[12px] text-brand-muted" title={p.next_step}>{p.next_step}</p>}
                          <select aria-label={`Mover a ${p.name} de etapa`} value={st.key} data-testid={`crm-move-${p.id}`}
                            onClick={(e) => e.stopPropagation()} onChange={(e) => moveTo(p, e.target.value)}
                            className="ca-select mt-2 h-8 w-full text-[12px]">
                            {CRM_STAGES.map((o) => <option key={o.key} value={o.key}>{o.key === st.key ? `Etapa: ${o.label}` : `Mover a ${o.label}`}</option>)}
                          </select>
                        </article>
                      );
                    })}
                    {col.length === 0 && <p className="px-1.5 py-6 text-center text-[12px] text-[var(--ca-subtle)]">Arrastra aquí</p>}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

      {items && items.length > 0 && view === "lista" && (
        <>
          <p className="ca-eyebrow"><span className="tnum">{items.length}</span> {items.length === 1 ? "contacto" : "contactos"}</p>
          {picked.size > 0 && (
            <div data-testid="crm-bulk-bar" className="ca-glass sticky top-16 z-10 flex flex-wrap items-center gap-3 rounded-xl px-4 py-3">
              <b className="text-sm text-[var(--ca-text)]"><span className="tnum">{picked.size}</span> {picked.size === 1 ? "seleccionado" : "seleccionados"}</b>
              <select data-testid="crm-bulk-move" className="ca-select h-9 max-w-[220px] text-sm" value="" onChange={(e) => bulkMove(e.target.value)}>
                <option value="">Mover a etapa…</option>
                {CRM_STAGES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <button type="button" onClick={bulkDelete} data-testid="crm-bulk-delete" className="ca-btn-line ca-btn-line-sm"><Trash2 size={14} className="mr-1" /> Eliminar</button>
              <button type="button" onClick={() => setPicked(new Set())} className="ml-auto text-[var(--ca-subtle)] hover:text-[var(--ca-text)]" aria-label="Quitar selección"><X size={16} /></button>
            </div>
          )}
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-brand-line bg-brand-panel md:block">
            <table className="w-full text-sm" data-testid="crm-table">
              <thead>
                <tr className="border-b border-brand-line text-left text-xs font-bold uppercase tracking-wide text-brand-muted">
                  <th className="w-10 py-3 pl-4"><input type="checkbox" aria-label="Seleccionar todos" data-testid="crm-pick-all" checked={!!allPicked}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(items.map((p) => p.id)))} style={{ accentColor: "var(--ca-gold)" }} /></th>
                  <th className="px-4 py-3 font-bold">Contacto</th>
                  <th className="px-4 py-3 font-bold">Red</th>
                  <th className="px-4 py-3 font-bold">Estado</th>
                  <th className="px-4 py-3 font-bold">Siguiente paso</th>
                  <th className="px-4 py-3 text-right font-bold">Encaje</th>
                  <th className="px-4 py-3 text-right font-bold">Últ. acción</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const sb = statusBadge(p.status);
                  return (
                    <tr key={p.id} onClick={() => openRow(p)} data-testid={`crm-row-${p.id}`} className={`cursor-pointer border-b border-brand-line last:border-0 transition-colors duration-150 hover:bg-brand-accent/[0.04] ${picked.has(p.id) ? "bg-brand-accent/[0.06]" : ""}`}>
                      <td className="w-10 py-3 pl-4" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Seleccionar ${p.name}`} data-testid={`crm-pick-${p.id}`} checked={picked.has(p.id)} onChange={() => togglePick(p.id)} style={{ accentColor: "var(--ca-gold)" }} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <InitialsAvatar name={p.name} size={36} />
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-brand-ink"><LeadMark p={p} />{p.name}</p>
                            <p className="truncate text-xs text-brand-muted">{p.company}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-brand-muted">{CHANNEL_META[p.channel]?.label}</td>
                      <td className="px-4 py-3"><span className={sb.cls}>{sb.label}</span></td>
                      <td className="max-w-[260px] px-4 py-3"><p className="line-clamp-2 text-brand-muted" title={p.next_step || ""}>{p.next_step || "—"}</p></td>
                      <td className="px-4 py-3 text-right font-semibold text-brand-ink tnum">{p.fit_score}</td>
                      <td className="px-4 py-3 text-right text-xs text-brand-muted tnum">{relativeTime(p.last_action_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {items.map((p) => {
              const sb = statusBadge(p.status);
              return (
                <div key={p.id} role="button" tabIndex={0} onClick={() => openRow(p)} onKeyDown={(e) => { if (e.key === "Enter") openRow(p); }} data-testid={`crm-card-${p.id}`} className="flex w-full items-center gap-3 rounded-lg border border-brand-line bg-brand-panel p-4 text-left transition-all duration-150 active:scale-[0.99]">
                  <input type="checkbox" aria-label={`Seleccionar ${p.name}`} checked={picked.has(p.id)} onClick={(e) => e.stopPropagation()} onChange={() => togglePick(p.id)} style={{ accentColor: "var(--ca-gold)", width: 18, height: 18, flex: "none" }} />
                  <InitialsAvatar name={p.name} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-brand-ink"><LeadMark p={p} />{p.name}</p>
                    <p className="truncate text-xs text-brand-muted">{CHANNEL_META[p.channel]?.label} · {p.company}</p>
                    <div className="mt-1.5"><span className={sb.cls}>{sb.label}</span></div>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-brand-ink tnum">{p.fit_score}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <ProspectForm open={formOpen} onOpenChange={setFormOpen} onCreated={onCreated} />
      <ProspectDrawer prospect={selected} open={drawerOpen} onOpenChange={setDrawerOpen} onUpdated={onUpdated} onDeleted={onDeleted} />
    </div>
  );
}
